import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { CycleRunProgressDto, GenerateDraftRun } from "@/lib/api";
import type { ActiveGenerateDraftRun } from "./generateDraftRun";
import { CYCLE_QUERY_KEYS } from "./constants";

/**
 * Gerencia side effects de notificação da geração de rascunho:
 *
 * 1. Invalidação antecipada: quando o progresso (poll rápido, 1.5s) detecta
 *    conclusão, invalida os caches imediatamente — sem esperar o poll lento do run (15s).
 *
 * 2. Toast com detalhes finais: só exibe quando o run (fonte de verdade) atualiza,
 *    trazendo `generatedCount` e `skippedCount`.
 */
export function useGenerateDraftNotifications(
  activeRun: ActiveGenerateDraftRun | null,
  run: GenerateDraftRun | null,
  progress: CycleRunProgressDto | null,
  invalidateCycles: () => void,
) {
  const queryClient = useQueryClient();
  const notifiedRunIdRef = useRef<number | null>(null);

  // Invalidação antecipada via progress (1.5s)
  useEffect(() => {
    if (!progress || !activeRun) return;
    if (progress.runId === notifiedRunIdRef.current) return;

    if (progress.status === "Completed") {
      invalidateCycles();
      queryClient.invalidateQueries({
        queryKey: CYCLE_QUERY_KEYS.generateDraftRuns,
      });
      queryClient.invalidateQueries({
        queryKey: CYCLE_QUERY_KEYS.generateDraftRun(activeRun.runId),
      });
      notifiedRunIdRef.current = progress.runId;
    }
  }, [progress, activeRun, invalidateCycles, queryClient]);

  // Toast com detalhes finais via run (15s)
  useEffect(() => {
    if (!run) return;
    if (run.runId === notifiedRunIdRef.current) return;

    if (run.status === "Completed") {
      toast.success(
        `Rascunho gerado: ${run.generatedCount} vínculo(s) criado(s), ${run.skippedCount} ignorado(s).`,
      );
      notifiedRunIdRef.current = run.runId;
    } else if (run.status === "Failed") {
      toast.error(
        `Falha ao gerar rascunho ${run.sourceYear} → ${run.targetYear}: ${run.errorMessage || "erro desconhecido"}.`,
      );
      notifiedRunIdRef.current = run.runId;
    }
  }, [run]);
}
